import { useState, useEffect } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { fetchCentersFromDB, isAdminUser } from "@/lib/api";
import { 
  Calendar, 
  CalendarDays,
  Users, 
  Wallet, 
  FileSpreadsheet, 
  FileText, 
  ChefHat, 
  LogOut, 
  Menu, 
  X,
  Building2,
  Clock,
  MessageCircle,
  Settings,
  Briefcase,
  IndianRupee,
  Receipt,
  Banknote,
  Tags,
  ChevronDown,
  ChevronRight,
  UserCog,
  Shield,
  Store,
  BarChart3,
  Globe,
  ArrowRightLeft,
  Database,
  UtensilsCrossed,
  LayoutGrid,
  Ban,
  Cog,
  FileCheck2,
  BookOpen,
  MessageSquare,
  Download,
  Share2,
  Upload,
  Sparkles,
  Activity,
  Calculator,
  Crown,
  Scale,
  Utensils,
  PartyPopper,
  Bike,
  Unlock,
  Lock,
  Check,
  Package,
  Phone,
  Plus,
} from "lucide-react";

// Import pages
import Attendance from "@/pages/Attendance";
import Employees from "@/pages/Employees";
import Salary from "@/pages/Salary";
import DutyRoster from "@/pages/DutyRoster";
import BhojanGuru from "@/pages/BhojanGuru";
import GuestResponse from "@/pages/GuestResponse";
import RecipeAdmin from "@/pages/RecipeAdmin";
import HRLetters from "@/pages/HRLetters";
import VisaHelper from "@/pages/VisaHelper";
import CentersManagement from "@/pages/CentersManagement";
import ManagersManagement from "@/pages/ManagersManagement";
import LoginSecurityAdmin from "@/pages/LoginSecurityAdmin";
import ConsumptionUnlockControl from "@/pages/ConsumptionUnlockControl";
import PriceUpdateApprovals from "@/pages/PriceUpdateApprovals";
import GiftVoucherVerify from "@/pages/GiftVoucherVerify";
import SalesExpenses from "@/pages/SalesExpenses";
import ExpenseHeads from "@/pages/ExpenseHeads";
import DeliveryChannels from "@/pages/DeliveryChannels";
import RoleManagement from "@/pages/RoleManagement";
import MyLoginSecurity from "@/pages/MyLoginSecurity";
import FranchiseManagement from "@/pages/FranchiseManagement";
import FranchiseEnquiries from "@/pages/FranchiseEnquiries";
import FranchiseExit from "@/pages/FranchiseExit";
import FranchiseOnboarding from "@/pages/FranchiseOnboarding";
import ConfigurationMaster from "@/pages/ConfigurationMaster";
import MasterSheets from "@/pages/MasterSheets";
import MISDashboard from "@/pages/MISDashboard";
// Phase-3 master dashboards (Feb-2026 architecture refactor) — 3 persona-
// focused hubs that consume the Financial Engine exclusively.
import CADashboard from "@/pages/CADashboard";
import FranchisorDashboard from "@/pages/FranchisorDashboard";
import CenterAccounts from "@/pages/CenterAccounts";
import GSTReconciliation from "@/pages/GSTReconciliation";
import BankReconciliation from "@/pages/BankReconciliation";
import HistoricalImport from "@/pages/HistoricalImport";
import OwnerReports from "@/pages/OwnerReports";
import AdCreator from "@/pages/AdCreator";
import CenterHealth from "@/pages/CenterHealth";
import GstPaidReport from "@/pages/GstPaidReport";
import ExpenseAdjustmentsReport from "@/pages/ExpenseAdjustmentsReport";
import LoanEntries from "@/pages/LoanEntries";
import BookingIntelligence from "@/pages/BookingIntelligence";
import WhatsAppSalesIntelligence from "@/pages/WhatsAppSalesIntelligence";
import TiffinBookings from "@/pages/TiffinBookings";
import CateringOrders from "@/pages/CateringOrders";
import EventBookings from "@/pages/EventBookings";
import MenuMaster from "@/pages/MenuMaster";
import AttendanceDashboard from "@/pages/AttendanceDashboard";
import InternationalAttendance from "@/pages/InternationalAttendance";
import EmployeeTransfers from "@/pages/EmployeeTransfers";
import MasterDataManagement from "@/pages/MasterDataManagement";
import ActivityLog from "@/pages/ActivityLog";
import FranchiseOwnerDashboard from "@/pages/FranchiseOwnerDashboard";
import MenuManagement from "@/pages/MenuManagement";
import POSBilling from "@/pages/POSBilling";
import BillingConfiguration from "@/pages/BillingConfiguration";
import DocumentManagement from "@/pages/DocumentManagement";
import CentreCallMonitor from "@/pages/CentreCallMonitor";
import UserManuals from "@/pages/UserManuals";
import FoodSafety from "@/pages/FoodSafety";
import DailyTextGenerator from "@/pages/DailyTextGenerator";
import SocialMediaPlanner from "@/pages/SocialMediaPlanner";
import BillDownload from "@/pages/BillDownload";
import InternationalRoster from "@/pages/InternationalRoster";
import MenuConfig from "@/pages/MenuConfig";
import ConsumptionFoodCost from "@/pages/ConsumptionFoodCost";
import InventoryAssets from "@/pages/InventoryAssets";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Menu categories structure
const menuCategories = [
  {
    id: "attendance",
    label: "Attendance",
    icon: Calendar,
    roleKey: "attendance",
    items: [
      { path: "/", icon: Calendar, label: "India Centers", roleKey: "attendance" },
      { path: "/international-attendance", icon: Globe, label: "International", roleKey: "attendance", forInternational: true },
      { path: "/international-roster", icon: CalendarDays, label: "Intl Roster", roleKey: "attendance", forInternational: true },
      { path: "/employee-transfers", icon: ArrowRightLeft, label: "Transfers", roleKey: "attendance" },
      { path: "/attendance-dashboard", icon: BarChart3, label: "Attendance Dashboard", forAdmin: true },
      { path: "/duty-roster", icon: Calendar, label: "Daily Duty Roster", roleKey: "attendance" },
    ]
  },
  {
    id: "sales",
    label: "Sales & Cash",
    icon: IndianRupee,
    roleKey: "sales_cash",
    items: [
      { path: "/sales", icon: IndianRupee, label: "Sales Dashboard", roleKey: "sales_cash" },
      { path: "/consumption-food-cost", icon: UtensilsCrossed, label: "Consumption & Food Cost", roleKey: "sales_cash" },
      { path: "/inventory-assets", icon: Package, label: "Inventory & Assets", roleKey: "sales_cash" },
      { path: "/center-health", icon: Activity, label: "Center Health Dashboard", roleKey: "sales_cash" },
      { path: "/expense-heads", icon: Tags, label: "Expense Heads", forMGT: true },
      { path: "/daily-text", icon: MessageSquare, label: "Sales Text Generator", roleKey: "sales_cash" },
    ]
  },
  {
    id: "operations",
    label: "Operations",
    icon: ChefHat,
    roleKey: "operations",
    items: [
      { path: "/booking-intelligence", icon: CalendarDays, label: "Booking Intelligence", roleKey: "operations" },
      { path: "/whatsapp-intel", icon: MessageCircle, label: "WhatsApp Intelligence", roleKey: "operations" },
      { path: "/menu-master", icon: UtensilsCrossed, label: "Menu Master", roleKey: "operations" },
      { path: "/bhojan-guru", icon: ChefHat, label: "Bhojan Guru", roleKey: "operations" },
      { path: "/guest-response", icon: MessageCircle, label: "Guest Response", roleKey: "operations" },
      { path: "/recipe-admin", icon: Settings, label: "Recipe Admin", roleKey: "operations" },
      { path: "/ad-creator", icon: Sparkles, label: "Center Manager Ad Creator" },
    ]
  },
  {
    id: "accounts",
    label: "Accounts",
    icon: BarChart3,
    forAccounts: true,
    items: [
      // Phase-3 master dashboards — single source of truth.
      { path: "/center-accounts", icon: Building2, label: "Center Accounts", forAccounts: true },
      { path: "/center-health", icon: Activity, label: "Center Health Dashboard", forAccounts: true },
      { path: "/expense-adjustments-report", icon: Scale, label: "Expense Adjustments Report", forAccounts: true },
      { path: "/gst-paid-report", icon: Calculator, label: "GST Paid Report", forAccounts: true },
      { path: "/gst-reconciliation", icon: Receipt, label: "GST Reconciliation", forAccounts: true },
      { path: "/admin/delivery-channels", icon: Bike, label: "Delivery Channels", forAccounts: true },
      { path: "/bank-reconciliation", icon: Banknote, label: "Bank Reconciliation", forAccounts: true },
      { path: "/loan-entries", icon: Wallet, label: "Loan Entries", forAccounts: true },
      { path: "/historical-import", icon: Upload, label: "Historical Import", forAccounts: true, superAdminOnly: true },
      { path: "/mis-dashboard", icon: BarChart3, label: "MIS Dashboard", forAccounts: true },
    ]
  },
  {
    id: "hr",
    label: "HR Management",
    icon: Users,
    roleKey: "hr",
    forMGT: true,
    items: [
      { path: "/employees", icon: Users, label: "Employees", roleKey: "hr" },
      { path: "/salary", icon: FileSpreadsheet, label: "Salary", roleKey: "hr" },
      { path: "/payslips", icon: FileText, label: "Payslips", roleKey: "hr" },
      { path: "/hr-letters", icon: Briefcase, label: "HR Letters", roleKey: "hr" },
      { path: "/visa-helper", icon: Globe, label: "Visa Helper", superAdminOnly: true },
    ]
  },
  {
    id: "marketing",
    label: "Marketing / Creative Studio",
    icon: Sparkles,
    items: [
      { path: "/social-media", icon: Share2, label: "Social Media Planner", forMGT: true },
    ]
  },
  {
    id: "mgt",
    label: "Management",
    icon: Building2,
    roleKey: "mgt",
    forMGT: true,
    items: [
      { path: "/centers", icon: Building2, label: "Centers", forMGT: true },
      { path: "/centre-call-monitor", icon: Phone, label: "Centre Call Monitor", forMGT: true },
      { path: "/managers", icon: UserCog, label: "Managers", forMGT: true },
      { path: "/login-security-admin", icon: Shield, label: "Login Security", superAdminOnly: true },
      { path: "/role-management", icon: Shield, label: "Role Management", forMGT: true },
      { path: "/my-login-security", icon: Lock, label: "My Login & Security", superAdminOnly: true },
      { path: "/master-data", icon: Database, label: "Master Data", forMGT: true },
      { path: "/activity-log", icon: Activity, label: "Login & Activity Log", forMGT: true },
      { path: "/consumption-unlock", icon: Unlock, label: "Consumption Unlock Control", forMGT: true },
      { path: "/price-approvals", icon: Check, label: "Price Update Approvals", forMGT: true },
      { path: "/menu-config", icon: LayoutGrid, label: "Menu Customization", superAdminOnly: true },
      { path: "/social-media", icon: Share2, label: "Social Media Planner", forMGT: true },
    ]
  },
  {
    id: "franchise",
    label: "Franchise",
    icon: Store,
    forFranchise: true,
    items: [
      { path: "/franchises", icon: Store, label: "Franchise Management", forFranchise: true, franchiseAdminOnly: true },
      { path: "/franchise-onboarding", icon: Plus, label: "New Franchise Onboarding", forFranchise: true, franchiseAdminOnly: true },
      { path: "/franchise-enquiries", icon: FileText, label: "Franchise Enquiries & Quotes", forFranchise: true, franchiseAdminOnly: true },
      { path: "/master-sheets", icon: Plus, label: "Master Sheets (PO / Invoice)", forFranchise: true, franchiseAdminOnly: true },
      { path: "/owner-reports", icon: FileText, label: "Owner Reports", forFranchise: true },
      { path: "/franchise-exit", icon: FileText, label: "Exit & Closure", forFranchise: true },
      { path: "/bill-download", icon: Download, label: "Bill Download", forFranchise: true },
      { path: "/gst-paid-report", icon: Calculator, label: "GST Paid Report", forFranchise: true },
      { path: "/documents", icon: FileCheck2, label: "Documents", forFranchise: true, franchiseAdminOnly: true },
    ]
  },
  {
    id: "billing",
    label: "Billing / POS",
    icon: Receipt,
    roleKey: "billing",
    forAdmin: true,
    items: [
      { path: "/pos-billing", icon: Receipt, label: "POS / Billing", roleKey: "billing", forAdmin: true },
      { path: "/billing-config", icon: Cog, label: "Configuration", roleKey: "billing", forAdmin: true },
      { path: "/menu-management", icon: UtensilsCrossed, label: "Menu Items", roleKey: "billing", forAdmin: true },
    ]
  },
  {
    id: "food_safety",
    label: "Food Safety",
    icon: Shield,
    forInternational: true,
    items: [
      { path: "/food-safety", icon: Shield, label: "Food Safety", forInternational: true },
    ]
  },
  {
    id: "help",
    label: "Help & Resources",
    icon: BookOpen,
    items: [
      { path: "/user-manuals", icon: BookOpen, label: "User Manuals" },
    ]
  },
];

export default function Dashboard() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(["attendance", "sales", "hr", "marketing", "mgt", "franchise", "operations", "billing", "food_safety", "help", "accounts"]);
  const [centersList, setCentersList] = useState([]);
  const [menuOverrides, setMenuOverrides] = useState({ categories: {}, items: {} });
  
  // Fetch centers from DB on mount
  useEffect(() => {
    if (session?.token) {
      fetchCentersFromDB(session.token).then(setCentersList);
    }
  }, [session?.token]);

  // Fetch menu overrides on mount + on window focus (so non-Super-Admins see
  // changes immediately after a Super Admin saves, without re-login).
  useEffect(() => {
    if (!session?.token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/menu-config?token=${encodeURIComponent(session.token)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setMenuOverrides({
            categories: data.categories || {},
            items: data.items || {},
          });
        }
      } catch (_) { /* ignore */ }
    };
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const interval = setInterval(load, 60000); // refresh every minute as safety net
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, [session?.token]);
  
  // Check user access levels (RBAC-driven, no hardcoding)
  const isSuperAdmin = session?.is_super_admin === true;
  const isAdmin = isAdminUser(session);

  // ── Global screen tracker: fire /api/activity-log/track on every route change ──
  const location = useLocation();
  useEffect(() => {
    if (!session?.token) return;
    if (!location?.pathname) return;
    // Skip nuisance paths
    if (location.pathname === "/" || location.pathname === "") return;
    // Derive a human-readable screen label from the last visible page title if possible
    const label = document.title && document.title !== "React App" ? document.title : location.pathname;
    fetch(`${API}/api/activity-log/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: session.token,
        event_type: "screen_opened",
        screen: label,
        details: { path: location.pathname },
      }),
    }).catch(() => {});
  }, [location?.pathname, session?.token]);
  
  // Get user's role permissions from session (assigned by Super Admin)
  // Super Admin gets all access, others get their assigned roles
  // NEW: Accounting role gets sales_cash access to ALL centers
  const userRoles = isSuperAdmin ? {
    attendance: true,
    sales_cash: true,
    hr: true,
    mgt: true,
    operations: true,
    view_all_centers: true,
    accounting: true,
    franchise: true,
    billing: true
  } : (session?.roles || {});
  
  // Check if user has accounting role (can view all centers in Sales & Cash)
  const hasAccountingRole = userRoles.accounting === true;

  // Map session/userRoles → set of role identifiers used by Menu Customization.
  // This is the single mapping every override role-check goes through.
  const userRoleKeys = (() => {
    const keys = new Set();
    if (isSuperAdmin) keys.add("super_admin");
    if (isAdmin) { keys.add("admin"); keys.add("mgt"); }
    if (userRoles.accounting === true) keys.add("accounting");
    if (userRoles.attendance === true) keys.add("attendance");
    if (userRoles.sales_cash === true) keys.add("sales_cash");
    if (userRoles.hr === true) keys.add("hr");
    if (userRoles.operations === true) keys.add("operations");
    if (userRoles.franchise === true) keys.add("franchise");
    if (userRoles.billing === true) keys.add("billing");
    if (userRoles.mgt === true) keys.add("mgt");
    const rk = session?.role_key || "";
    if (rk) keys.add(rk);
    if (rk === "franchise_owner") keys.add("franchise");
    // International matches users from non-India centers
    const centerCode = session?.center || "";
    const isIntl = session?.is_india_center === false ||
      (session?.is_india_center !== true && (centersList.find(c => c.code === centerCode)?.is_india_center === false));
    if (isIntl) keys.add("international");
    return keys;
  })();

  // Returns an override-driven access decision, or null if no override exists.
  // visible_roles=[] (empty array) explicitly hides the entry from everyone except Super Admin.
  const overrideAccess = (overrideEntry) => {
    if (!overrideEntry || overrideEntry.visible_roles === undefined) return null;
    if (isSuperAdmin) return true; // Super admin always sees everything (no lock-out)
    const allowed = overrideEntry.visible_roles || [];
    if (allowed.length === 0) return false;
    return allowed.some(r => userRoleKeys.has(r));
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Check if user has access to an item based on their assigned roles
  const hasAccess = (item) => {
    // Super Admin override check first (an override may explicitly hide for everyone but SA)
    const itemOverride = menuOverrides.items?.[item.path];
    const ov = overrideAccess(itemOverride);
    if (ov !== null) return ov;
    if (isSuperAdmin) return true; // Super Admin has full access
    if (item.forMGT) return isAdmin; // Management items require Admin access
    if (item.forAdmin) {
      // Admin-only items can also be accessed via roleKey assignment
      if (isAdmin || isSuperAdmin) return true;
      if (item.roleKey && userRoles[item.roleKey] === true) return true;
      return false;
    }
    if (item.forInternational) {
      // International items: visible to Admin, Super Admin, and international center managers
      // NOT visible to India center managers
      if (isAdmin || isSuperAdmin) return true;
      // Check session for center info - allow if not an India center
      // We rely on the backend to reject India centers, but hide from sidebar too
      const centerCode = session?.center || "";
      // If center has is_india_center flag from session, use it
      if (session?.is_india_center === false) return true;
      if (session?.is_india_center === true) return false;
      // Default: use center data from DB to determine if India center
      const centerData = centersList.find(c => c.code === centerCode);
      if (centerData) return centerData.is_india_center === false;
      return false; // Default hide for unknown centers
    }
    if (item.forFranchise) {
      // Owner Reports should be visible to anyone who can RELEASE reports
      // (Admin, Super Admin, Accountant) AND to Franchise Owners.
      const isAccountant = userRoles.accounting === true || userRoles.accounts === true || session?.role_key === 'accountant';
      if (item.path === '/owner-reports') {
        return isAdmin || isSuperAdmin || isAccountant || userRoles.franchise === true || session?.role_key === 'franchise_owner';
      }
      const hasFranchiseAccess = isAdmin || userRoles.franchise === true;
      if (!hasFranchiseAccess) return false;
      // Franchise owners (non-admin users with franchise role) can only see Owner Dashboard + Exit
      // franchiseAdminOnly items are hidden for non-admin franchise users
      if (item.franchiseAdminOnly) {
        const isFranchiseOnlyUser = !isAdmin && !isSuperAdmin && (session?.role_key === "franchise_owner" || userRoles.franchise === true);
        if (isFranchiseOnlyUser) return false;
      }
      return true;
    }
    if (item.superAdminOnly && !isSuperAdmin) return false;
    if (item.forAccounts) {
      return isAdmin || userRoles.accounting === true;
    }
    if (item.roleKey) {
      if (item.roleKey === "sales_cash" && userRoles.accounting) {
        return true;
      }
      return userRoles[item.roleKey] === true;
    }
    return true;
  };

  // Check if user has access to a category
  const hasCategoryAccess = (category) => {
    const catOverride = menuOverrides.categories?.[category.id];
    const ov = overrideAccess(catOverride);
    if (ov !== null) return ov;
    if (isSuperAdmin) return true;
    if (category.forMGT) return isAdmin;
    if (category.forInternational) {
      if (isAdmin) return true;
      const centerCode = session?.center || "";
      if (session?.is_india_center === false) return true;
      if (session?.is_india_center === true) return false;
      const centerData = centersList.find(c => c.code === centerCode);
      if (centerData) return centerData.is_india_center === false;
      return false;
    }
    if (category.forFranchise) {
      // Franchise category accessible to Admin, Franchise Owners, or Accountants
      // (since Accountants now release Owner Reports from within this section).
      const isAccountant = userRoles.accounting === true || userRoles.accounts === true || session?.role_key === 'accountant';
      const hasFranchiseAccess = isAdmin || userRoles.franchise === true || isAccountant;
      if (!hasFranchiseAccess) return false;
      // Check at least one item is accessible
      return category.items.some(item => hasAccess(item));
    }
    if (category.roleKey) {
      // Special case: Accounting role gets sales_cash category access
      if (category.roleKey === "sales_cash" && userRoles.accounting) {
        return true;
      }
      return userRoles[category.roleKey] === true;
    }
    return true;
  };

  // Apply DB-driven menu overrides:
  //  - re-parent items (if override.category_id differs from default)
  //  - reorder categories and items by override.order (smaller first; nulls last, stable original order)
  //  - then apply role-based access filtering
  const applyMenuOverrides = () => {
    // Build a fresh structure where items can be moved across categories
    const itemOverrides = menuOverrides.items || {};
    const catOverrides = menuOverrides.categories || {};

    // Phase 1: gather items grouped by their effective parent category.
    const itemsByCat = {};
    menuCategories.forEach(cat => { itemsByCat[cat.id] = []; });
    menuCategories.forEach((cat, catIdx) => {
      cat.items.forEach((item, itemIdx) => {
        const ov = itemOverrides[item.path] || {};
        const parent = ov.category_id && itemsByCat[ov.category_id] !== undefined ? ov.category_id : cat.id;
        const order = ov.order !== undefined && ov.order !== null ? ov.order : (catIdx * 1000 + itemIdx);
        itemsByCat[parent].push({ ...item, _order: order });
      });
    });

    // Phase 2: sort items inside each category by _order, then strip _order.
    Object.keys(itemsByCat).forEach(cid => {
      itemsByCat[cid].sort((a, b) => (a._order ?? 0) - (b._order ?? 0));
      itemsByCat[cid] = itemsByCat[cid].map(({ _order, ...rest }) => rest);
    });

    // Phase 3: produce an ordered list of categories with their items, applying category override.order.
    const catsWithOrder = menuCategories.map((cat, idx) => {
      const ov = catOverrides[cat.id] || {};
      const order = ov.order !== undefined && ov.order !== null ? ov.order : idx;
      return { ...cat, _order: order, items: itemsByCat[cat.id] || [] };
    });
    catsWithOrder.sort((a, b) => (a._order ?? 0) - (b._order ?? 0));
    return catsWithOrder.map(({ _order, ...rest }) => rest);
  };

  const orderedCategories = applyMenuOverrides();

  // Filter categories and items based on access
  const filteredCategories = orderedCategories
    .filter(cat => hasCategoryAccess(cat))
    .map(cat => ({
      ...cat,
      items: cat.items.filter(item => hasAccess(item))
    }))
    .filter(cat => cat.items.length > 0);

  const handleLogout = () => {
    // Best-effort logout tracking — don't block navigation on failure
    if (session?.token) {
      fetch(`${API}/api/activity-log/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token }),
        keepalive: true,
      }).catch(() => {});
    }
    logout();
    navigate("/login");
  };

  const currentNav = filteredCategories.flatMap((c) => c.items).find((i) => (i.path === "/" ? location.pathname === "/" : location.pathname.startsWith(i.path)))
    || { label: { "/configuration-master": "Configuration Master", "/dashboard/ca": "CA Dashboard", "/dashboard/franchisor": "Franchisor Dashboard", "/dashboard/franchise-owner": "Franchise Owner Dashboard",
      "/franchise-dashboard": "Franchise Dashboard", "/tiffin-bookings": "Tiffin Bookings", "/catering-orders": "Catering Orders", "/event-bookings": "Celebration Bookings", "/verify-voucher": "Verify Gift Voucher" }[location.pathname] };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        data-testid="mobile-menu-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X /> : <Menu />}
      </Button>

      {/* Sidebar overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:sticky top-0 left-0 h-screen w-64 sidebar z-50 transition-transform duration-300 flex flex-col",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <h1 className="text-2xl font-bold text-white">Purnabramha</h1>
          <p className="text-white/60 text-sm mt-1">IntraPB Portal</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {filteredCategories.map((category) => (
            <div key={category.id} className="space-y-1">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-2">
                  <category.icon className="w-4 h-4" />
                  <span>{category.label}</span>
                </div>
                {expandedCategories.includes(category.id) 
                  ? <ChevronDown className="w-4 h-4" />
                  : <ChevronRight className="w-4 h-4" />
                }
              </button>
              
              {/* Category Items */}
              {expandedCategories.includes(category.id) && (
                <div className="ml-4 space-y-1">
                  {category.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === "/"}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => cn(
                        "sidebar-item text-sm",
                        isActive && "active"
                      )}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User info & Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 mb-3">
            <Building2 className="w-5 h-5 text-secondary" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">
                {session?.center}
              </p>
              <p className="text-white/60 text-xs truncate">
                {session?.managerName}
              </p>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 min-h-screen lg:ml-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b-2 border-[#D9AE52] bg-[#FFF9EE]/90 backdrop-blur-md">
          <div className="flex items-center justify-between px-6 py-3 lg:px-8">
            <div className="flex items-center gap-4 lg:gap-6">
              <div className="lg:hidden w-10" /> {/* Spacer for mobile menu */}
              <div className="flex items-center gap-3">
                {currentNav?.icon && <span className="hidden sm:grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-b from-[#8f1524] to-[#4D0712] text-[#F4D58D] shadow"><currentNav.icon className="h-6 w-6" /></span>}
                <div>
                  <h2 className="font-serif text-xl md:text-2xl font-bold text-[#750817] leading-tight" data-testid="rm-page-title">
                    {currentNav?.label || session?.center}
                  </h2>
                  <p className="text-sm text-[#5a3a22]">
                    <b>{session?.center}</b> · {session?.managerName}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>{new Date().toLocaleDateString()}</span>
              </div>
              {isSuperAdmin && (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-500/20 text-red-500 border border-red-500/30">
                  Super Admin
                </span>
              )}
              {!isSuperAdmin && isAdmin && (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-500/20 text-purple-500 border border-purple-500/30">
                  Admin
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-8 animate-fadeIn">
          <Routes>
            <Route path="/" element={<Attendance />} />
            <Route path="/international-attendance" element={<InternationalAttendance />} />
            <Route path="/international-roster" element={<InternationalRoster />} />
            <Route path="/employee-transfers" element={<EmployeeTransfers />} />
            <Route path="/attendance-dashboard" element={<AttendanceDashboard />} />
            <Route path="/duty-roster" element={<DutyRoster />} />
            <Route path="/sales" element={<SalesExpenses />} />
            <Route path="/expense-heads" element={<ExpenseHeads />} />
            <Route path="/admin/delivery-channels" element={<DeliveryChannels />} />
            <Route path="/guest-response" element={<GuestResponse />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/salary" element={<Salary />} />
            <Route path="/payslips" element={<Salary isPayslips />} />
            <Route path="/hr-letters" element={<HRLetters />} />
            <Route path="/visa-helper" element={<VisaHelper />} />
            <Route path="/centers" element={<CentersManagement />} />
            <Route path="/centre-call-monitor" element={<CentreCallMonitor />} />
            <Route path="/managers" element={<ManagersManagement />} />
            <Route path="/login-security-admin" element={<LoginSecurityAdmin />} />
            <Route path="/role-management" element={<RoleManagement />} />
            <Route path="/my-login-security" element={<MyLoginSecurity session={session} />} />
            <Route path="/franchises" element={<FranchiseManagement />} />
            <Route path="/franchise-onboarding" element={<FranchiseOnboarding />} />
            <Route path="/franchise-enquiries" element={<FranchiseEnquiries />} />
            <Route path="/configuration-master" element={<ConfigurationMaster />} />
            <Route path="/master-sheets" element={<MasterSheets />} />
            <Route path="/franchise-exit" element={<FranchiseExit />} />
            <Route path="/center-accounts" element={<CenterAccounts />} />
            <Route path="/center-health" element={<CenterHealth />} />
            <Route path="/gst-paid-report" element={<GstPaidReport />} />
            <Route path="/expense-adjustments-report" element={<ExpenseAdjustmentsReport />} />
            <Route path="/gst-reconciliation" element={<GSTReconciliation />} />
            <Route path="/bank-reconciliation" element={<BankReconciliation />} />
            <Route path="/historical-import" element={<HistoricalImport />} />
            <Route path="/owner-reports" element={<OwnerReports />} />
            <Route path="/ad-creator" element={<AdCreator />} />
            <Route path="/loan-entries" element={<LoanEntries />} />
            <Route path="/mis-dashboard" element={<MISDashboard />} />
            {/* Phase-3 master dashboards */}
            <Route path="/dashboard/ca" element={<CADashboard />} />
            <Route path="/dashboard/franchisor" element={<FranchisorDashboard />} />
            <Route path="/dashboard/franchise-owner" element={<FranchiseOwnerDashboard />} />
            <Route path="/booking-intelligence" element={<BookingIntelligence />} />
            <Route path="/whatsapp-intel" element={<WhatsAppSalesIntelligence session={session} />} />
            <Route path="/consumption-food-cost" element={<ConsumptionFoodCost />} />
            <Route path="/inventory-assets" element={<InventoryAssets />} />
            <Route path="/tiffin-bookings" element={<TiffinBookings />} />
            <Route path="/catering-orders" element={<CateringOrders />} />
            <Route path="/event-bookings" element={<EventBookings />} />
            <Route path="/menu-master" element={<MenuMaster />} />
            <Route path="/bhojan-guru" element={<BhojanGuru />} />
            <Route path="/recipe-admin" element={<RecipeAdmin />} />
            <Route path="/master-data" element={<MasterDataManagement />} />
            <Route path="/activity-log" element={<ActivityLog />} />
            <Route path="/consumption-unlock" element={<ConsumptionUnlockControl />} />
            <Route path="/price-approvals" element={<PriceUpdateApprovals />} />
            <Route path="/verify-voucher" element={<GiftVoucherVerify session={session} />} />
            <Route path="/menu-config" element={<MenuConfig />} />
            <Route path="/user-manuals" element={<UserManuals />} />
            <Route path="/menu-management" element={<MenuManagement />} />
            <Route path="/franchise-dashboard" element={<FranchiseOwnerDashboard />} />
            <Route path="/pos-billing" element={<POSBilling />} />
            <Route path="/billing-config" element={<BillingConfiguration />} />
            <Route path="/documents" element={<DocumentManagement />} />
            <Route path="/food-safety" element={<FoodSafety />} />
            <Route path="/daily-text" element={<DailyTextGenerator />} />
            <Route path="/social-media" element={<SocialMediaPlanner />} />
            <Route path="/bill-download" element={<BillDownload />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
